import React, { useState, useRef } from 'react';
import { uploadStoryVideo } from '../utils/videoUpload';
import { CloudArrowUpIcon, PlayIcon, XMarkIcon, CameraIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';

const VideoUpload = ({ 
  currentVideo, 
  onUploadSuccess, 
  storyId,
  className = '' 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(currentVideo || null);
  const [aspectWarning, setAspectWarning] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setError('');
    setAspectWarning('');

    // Check aspect ratio
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (h <= w) {
        setAspectWarning('For best results, record your video vertically (portrait mode) like Instagram Stories.');
      }
      URL.revokeObjectURL(video.src);
    };
    video.src = url;
    
    // Auto-upload on mobile devices
    if (window.innerWidth <= 768) {
      await handleUpload(file);
    }
  };

  const handleUpload = async (fileToUpload = null) => {
    const file = fileToUpload || fileInputRef.current?.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError('');
    
    try {
      await uploadStoryVideo({
        file,
        storyId,
        onProgress: (progress) => setUploadProgress(progress),
        onError: (err) => setError(err),
        onSuccess: (downloadURL, fullPath) => {
          console.log('[VideoUpload] onUploadSuccess called with:', downloadURL, fullPath);
          onUploadSuccess({ playableURL: downloadURL, storagePath: fullPath });
          setUploadProgress(100);
          // Clean up preview URL
          if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
          }
        }
      });
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveVideo = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
    onUploadSuccess(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isMobile = window.innerWidth <= 768;

  return (
    <div className={`relative ${className}`}>
      {/* File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/mov,video/avi,video/quicktime,video/webm"
        onChange={handleFileSelect}
        className="hidden"
        disabled={isUploading}
      />
      
      {/* Camera Input for direct camera access */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="video/mp4,video/mov,video/avi,video/quicktime,video/webm"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        disabled={isUploading}
      />

      {/* Mobile-friendly vertical video hint */}
      <div className="flex items-center justify-center mb-2 gap-2 text-xs text-[#23406e] font-medium">
        <DevicePhoneMobileIcon className="h-5 w-5" />
        Hold your phone vertically for best results (Stories/Reels style)
      </div>

      {/* Upload Area */}
      {!previewUrl && (
        <div className="space-y-3">
          {/* Mobile-optimized upload buttons */}
          {isMobile ? (
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={isUploading}
                className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors bg-white"
              >
                <CameraIcon className="h-8 w-8 text-gray-400 mb-2" />
                <span className="text-sm font-medium text-gray-700">Record Video</span>
                <span className="text-xs text-gray-500 mt-1">Use Camera</span>
              </button>
              
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors bg-white"
              >
                <CloudArrowUpIcon className="h-8 w-8 text-gray-400 mb-2" />
                <span className="text-sm font-medium text-gray-700">Choose Video</span>
                <span className="text-xs text-gray-500 mt-1">From Library</span>
              </button>
            </div>
          ) : (
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-sm text-gray-600 mb-2">
                Click to upload a video or drag and drop
              </p>
              <p className="text-xs text-gray-500">
                MP4, MOV, AVI, WebM up to 50MB, max 5 minutes
              </p>
            </div>
          )}
          
          {/* File size and format info */}
          <div className="text-xs text-gray-500 text-center">
            <p>Maximum file size: 50MB • Maximum duration: 5 minutes</p>
            <p>Supported formats: MP4, MOV, AVI, WebM</p>
          </div>
        </div>
      )}

      {/* Preview Area */}
      {previewUrl && (
        <div className="relative">
          <div className="aspect-[9/16] w-full max-w-xs mx-auto rounded-xl overflow-hidden bg-black">
            <video
              src={previewUrl}
              className="w-full h-full object-cover"
              controls
              preload="metadata"
              playsInline
            />
          </div>
          
          {/* Aspect ratio warning */}
          {aspectWarning && (
            <div className="mt-2 text-xs text-yellow-700 bg-yellow-100 rounded p-2 text-center">
              {aspectWarning}
            </div>
          )}

          {/* Upload Button - Only show on desktop or if auto-upload failed */}
          {!isMobile && !isUploading && (
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => handleUpload()}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition"
              >
                Upload Video
              </button>
              <button
                type="button"
                onClick={handleRemoveVideo}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Progress Bar */}
          {isUploading && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Remove button for mobile after upload */}
          {isMobile && !isUploading && (
            <button
              type="button"
              onClick={handleRemoveVideo}
              className="mt-3 w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition"
            >
              Remove Video
            </button>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
          {error}
        </div>
      )}

      {/* File Info */}
      {fileInputRef.current?.files?.[0] && !isUploading && (
        <div className="mt-2 text-xs text-gray-500">
          File: {fileInputRef.current.files[0].name} ({formatFileSize(fileInputRef.current.files[0].size)})
        </div>
      )}
    </div>
  );
};

export default VideoUpload; 